import React, { useState } from "react";
import "./LandingPage.css";
import mascotImage from "../assets/mascot.jpeg";
import foxImage from "../assets/fox-mascot.png";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./../components/AuthContext";

function LandingPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });

  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  const handleHeaderLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleHeaderLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");

    if (!loginData.email || !loginData.password) {
      setLoginError("Email and password are required");
      return;
    }

    setLoginLoading(true);

    const result = await login(loginData.email, loginData.password);

    if (result.success) {
      navigate("/dashboard");
    } else {
      setLoginError(result.error || "Login failed");
    }

    setLoginLoading(false);
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="container">
          <div className="logo">
            <img
              src={mascotImage}
              alt="Job-Fax Mascot"
              className="logo-mascot"
            />
            Job-Fax
          </div>

          <nav className="nav">
            <a href="#how-it-works">How It Works</a>
            <a href="#pricing">Pricing</a>
            <a href="mailto:administrator@job-fax.com">Contact</a>

            <form className="header-login-form" onSubmit={handleHeaderLoginSubmit}>
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={loginData.email}
                onChange={handleHeaderLoginChange}
                disabled={loginLoading}
              />

              <input
                type="password"
                name="password"
                placeholder="Password"
                value={loginData.password}
                onChange={handleHeaderLoginChange}
                disabled={loginLoading}
              />

              <button type="submit" disabled={loginLoading}>
                {loginLoading ? "..." : "Login"}
              </button>
            </form>

            {loginError && (
              <span className="header-login-error">{loginError}</span>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-side-image hero-side-image-left">
              <img
                src={foxImage}
                alt="Job-Fax Fox"
                className="fox-main"
              />
            </div>

            <div className="hero-text">
              <h1>Job-Fax - WIN® Report for Job Postings</h1>
              <h2 className="coming-soon">Verify before you Apply!</h2>
              <p className="tagline">Publicly Gathered | Clearly Delivered</p>

              <div className="prep-explanation">
                <p>
                  Our Proprietary <strong>PREP® Detector</strong> provides comprehensive insights:
                </p>

                <div className="prep-grid">
                  <div className="prep-item">
                    <div className="prep-letter">P</div>
                    <span><b>Posting Analysis</b></span>
                  </div>

                  <div className="prep-item">
                    <div className="prep-letter">R</div>
                    <span><b>Recruiter Verification</b></span>
                  </div>

                  <div className="prep-item">
                    <div className="prep-letter">E</div>
                    <span><b>Employer Authentication</b></span>
                  </div>

                  <div className="prep-item">
                    <div className="prep-letter">P</div>
                    <span><b>Process Validation</b></span>
                  </div>
                </div>
              </div>

              <p className="verify-text">Verify as you apply to jobs.</p>

              <button
                className="cta-button"
                onClick={() => navigate("/register")}
              >
                Show my first job posting PREP® report!
              </button>
            </div>

            <div className="hero-image">
              <div className="mascot-display">
                <img
                  src={mascotImage}
                  alt="Job-Fax Detector"
                  className="mascot-main"
                />
                <div className="shield-badge">✓</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" id="how-it-works">
        <div className="container">
          <h2>How It Works</h2>

          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Submit Job URL</h3>
              <p>Paste any job posting URL you want to verify</p>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <h3>AI Analysis</h3>
              <p>Our PREP® system analyzes 4 layers of job indicators</p>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <h3>Get WIN® Report</h3>
              <p>Receive detailed insights and actionable information</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing" id="pricing">
        <div className="container">
          <h2>Simple, Transparent Pricing</h2>

          <div className="pricing-grid">
            <div className="price-card">
              <h3>Free</h3>
              <div className="price">$0<span> first day only</span></div>

              <ul>
                <li>5 job analyses per day</li>
                <li>Basic PREP® analysis</li>
              </ul>

              <button
                className="price-button secondary"
                onClick={() => navigate("/register")}
              >
                Get Started
              </button>
            </div>

            <div className="price-card featured">
              <h3>General</h3>
              <div className="price">$5.00<span>/week (+ taxes)</span></div>

              <ul>
                <li>20 job analyses a week</li>
                <li>Full PREP® analysis</li>
                <li>WIN® score for job posting</li>
              </ul>

              <button
                className="price-button primary"
                onClick={() => navigate("/register")}
              >
                Subscribe
              </button>
            </div>

            <div className="price-card">
              <h3>Pro</h3>
              <div className="price">$20.00<span>/week (+ taxes)</span></div>

              <ul>
                <li>100 job analyses a week</li>
                <li>Full PREP® analysis</li>
                <li>WIN® score + more for job posting</li>
              </ul>

              <button
                className="price-button secondary"
                onClick={() => navigate("/register")}
              >
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-content">
          <div className="footer-section footer-about-section">
            <h4>About</h4>

            <ul>
              <li><a href="#pricing">Pricing</a></li>
              <li><a
              href="/eula.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              EULA Terms of Service
            </a> </li>
              <li><a href="mailto:administrator@job-fax.com">Contact</a></li>
            </ul>
          </div>

          <div className="footer-section footer-disclaimer-section">
            <button
              className="legal-title-button"
              onClick={() => setDisclaimerOpen(true)}
            >
              Legal Disclaimer
            </button>

            <div className="footer-disclaimer">
              <p>
                <strong>About Us:</strong> Job-Fax ® © is a wholly owned subsidiary of Rjens LLC of Las Vegas, Nevada.
              </p>

              <p>
                We prioritize your data privacy by implementing strict security measures
                to protect your information at every stage. Our commitment extends to
                using advanced encryption protocols and continuous monitoring to ensure
                your data remains secure.
              </p>

              <p>
                Additionally, we regularly update our security practices and conduct
                thorough audits to maintain the highest standards of data protection.
                Our sources consist of publicly gathered information from the worldwide
                web, including employers with federal EINs and URLs pointing to job
                postings.
              </p>

              <p>
                Please note that legal disclaimers apply to the use of third-party
                content, and users should exercise due diligence when relying on such
                information.
              </p>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© 2025 Job-Fax. All rights reserved.</p>
        </div>
      </footer>

      {disclaimerOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="legal-title"
        >
          <div className="modal">
            <div className="modal-header">
              <h3 id="legal-title">Legal & Data Disclaimer</h3>

              <button
                className="modal-close"
                aria-label="Close"
                onClick={() => setDisclaimerOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>Full legal disclaimer coming soon.</p>
            </div>

            <div className="modal-footer">
              <button
                className="button primary"
                onClick={() => setDisclaimerOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;